import React, { useState, useEffect } from 'react';
import { useDatumStore } from '../../stores/datumStore';
import { updateDatumUser } from '../../api/datum';
import { useAuthStore } from '../../stores/authStore';
import { Card } from '../common/Card';
import { FormView } from '../forms/FormView';

interface FormWidgetProps {
  datumId: string;
  tenant: string;
  kind?: string;
  isPinned?: boolean;
  onPinToggle?: () => void;
  getDatum?: (id: string) => any;
  refreshDatum?: (tenant: string, id: string) => Promise<void>;
  updateDatumUser?: (tenant: string, datumId: string, data: Record<string, unknown>) => Promise<any>;
}

export function FormWidget({ datumId: datumId, tenant, isPinned, onPinToggle, getDatum: getDatumProp, refreshDatum: refreshDatumProp, updateDatumUser: updateDatumUserProp }: FormWidgetProps) {
  const getDatumStore = useDatumStore((state) => state.getDatum);
  const refreshDatumStore = useDatumStore((state) => state.refreshDatum);
  const user = useAuthStore((state) => state.user);

  const getDatum = getDatumProp || getDatumStore;
  const refreshDatum = refreshDatumProp || refreshDatumStore;
  const updateDatumUserFn = updateDatumUserProp || updateDatumUser;
  const datum = getDatum(datumId);

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedData, setLastSavedData] = useState<Record<string, unknown>>({});
  const [debounceTimer, setDebounceTimer] = useState<number | null>(null);

  // Load user data from latest datumUser
  useEffect(() => {
    if (!datum || !user) return;

    const myDatumUsers = datum.datumUsers
      .filter((du: any) => du.userId === user.id)
      .sort((a: any, b: any) => b.createdAt - a.createdAt);
    const latestDatumUser = myDatumUsers[0];
    const userData = latestDatumUser?.data || {};

    setFormData(userData);
    setLastSavedData(userData);
  }, [datum?.id, datum?.datumUsers?.length, user?.id]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  const handleSave = async () => {
    if (!datum) return;

    // Clear any pending debounce
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      setDebounceTimer(null);
    }

    setIsSaving(true);
    try {
      await updateDatumUserFn(tenant, datumId, formData);
      await refreshDatum(tenant, datumId);
      setLastSavedData(formData);
    } catch (error) {
      console.error('Failed to save form data:', error);
      // Revert on error
      setFormData(lastSavedData);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (newData: Record<string, unknown>) => {
    setFormData(newData);

    const changedKeys = Object.keys(newData).filter(
      key => newData[key] !== lastSavedData[key]
    );

    // Check if only single field changed
    const isSingleFieldChange = changedKeys.length === 1;
    const changedKey = changedKeys[0];
    const isCheckboxChange = isSingleFieldChange && changedKey?.startsWith('checkbox_');

    // Checkboxes save immediately
    if (isCheckboxChange) {
      // Clear any pending debounce
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        setDebounceTimer(null);
      }

      setIsSaving(true);
      updateDatumUserFn(tenant, datumId, newData)
        .then(() => refreshDatum(tenant, datumId))
        .then(() => setLastSavedData(newData))
        .catch((error) => {
          console.error('Failed to save form data:', error);
          setFormData(lastSavedData);
        })
        .finally(() => setIsSaving(false));
    }
    // All other single field changes save with debounce (numbers, text, etc)
    else if (isSingleFieldChange) {
      // Clear existing debounce timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      // Set new debounce timer (1 second)
      const timer = setTimeout(() => {
        setIsSaving(true);
        updateDatumUserFn(tenant, datumId, newData)
          .then(() => refreshDatum(tenant, datumId))
          .then(() => setLastSavedData(newData))
          .catch((error) => {
            console.error('Failed to save form data:', error);
            setFormData(lastSavedData);
          })
          .finally(() => setIsSaving(false));
      }, 1000);

      setDebounceTimer(timer);
    }
  };

  if (!datum) {
    return null;
  }

  const body = (datum.fixedData?.body as string) || '';

  return (
    <Card className="relative">
      {/* Pin button */}
      {onPinToggle && (
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={onPinToggle}
            className="text-gray-400 hover:text-gray-600"
            title={isPinned ? 'Unpin' : 'Pin'}
          >
            <svg
              className="w-5 h-5"
              fill={isPinned ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Form content */}
      <FormView
        markdown={body}
        formData={formData}
        onChange={handleChange}
        onSave={handleSave}
        isSaving={isSaving}
        autoSaveCheckboxes={true}
      />

      {/* Auto-save indicator for checkboxes */}
      {isSaving && (
        <div className="absolute top-2 right-2">
          <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </Card>
  );
}
